// /home/szymon/kreator2/frontend/src/components/editor/hooks/useEditorPersonalization.js
// v1.1.0 – Ujednolicono odczyt i zapis listy gości do historii projektu (jsonData)

import { useState, useEffect, useMemo, useCallback } from "react";

// === FUNKCJE POMOCNICZE DO OBSŁUGI STRUKTURY PERSONALIZACJI ===

/**
 * Konwertuje pole personalizacja do formatu tekstowego (dla wyświetlenia w edytorze)
 * Obsługuje zarówno starą strukturę (array of strings) jak i nową (array of objects)
 */
function personalizationToText(personalizacja) {
  if (!Array.isArray(personalizacja)) return "";
  
  return personalizacja.map(item => {
    if (typeof item === 'string') {
      // Stara struktura - zwróć string
      return item;
    } else if (typeof item === 'object' && item !== null && item.name) {
      // Nowa struktura - zwróć nazwę gościa
      return item.name;
    }
    return "";
  }).filter(Boolean).join("\n");
}

/**
 * Konwertuje tekst z edytora do nowej struktury personalizacji
 * Zachowuje istniejące QR URL-e jeśli gość już istnieje
 */
function textToPersonalization(text, existingPersonalizacja = []) {
  const names = text.split("\n").filter(Boolean);
  
  return names.map(name => {
    const trimmedName = name.trim();
    
    // Sprawdź czy gość już istnieje w obecnej strukturze
    if (Array.isArray(existingPersonalizacja)) {
      for (let item of existingPersonalizacja) {
        if (typeof item === 'object' && item !== null && item.name === trimmedName) {
          // Zachowaj istniejący URL QR
          return { name: trimmedName, qrUrl: item.qrUrl };
        }
      }
    }
    
    // Nowy gość - dodaj bez QR URL
    return { name: trimmedName, qrUrl: null };
  });
}

/**
 * Sprawdza czy gość ma przypisany QR URL
 */
function hasQrUrl(guestItem) {
  return typeof guestItem === 'object' && guestItem !== null && 
         typeof guestItem.qrUrl === 'string' && guestItem.qrUrl.length > 0;
}

// Funkcja pomocnicza do budowania URL API
const buildApiUrl = (base, pathSegments) => {
  const segments = [base.replace(/\/$/, ""), ...pathSegments];
  return segments.join("/");
};

export function useEditorPersonalization({
  pages,
  setPages, // Potrzebne do aktualizacji stanu po odczycie historii
  currentPage,
  panelMode,
  setPanelMode,
  publicMode,
  selectedShapeId,
  setSelectedShapeId,
  handleManualSave, // Funkcja z Editor.js do zapisu historii
  doAutoSave, // Funkcja z Editor.js do autosave historii
  changesCountRef,
  // --- NOWE ARGUMENTY ---
  projectId,
  backendUrl,
  publicAccessKey, // Klucz publiczny zamówienia (dla trybu klienta)
  downloadKey // Klucz pobierania pliku (dla trybu klienta)
}) {
  // const [isLoadingGuests, setIsLoadingGuests] = useState(false); // Stan ładowania nie jest już potrzebny tutaj

  // --- NOWA LOGIKA: Obliczanie indexu i tekstu za pomocą useMemo ---
  const personalizationPageIndex = useMemo(() => {
    if (!pages || pages.length === 0) return -1;
    for (let i = 0; i < pages.length; i++) {
      const pg = pages[i];
      const hasPerso =
        (pg.texts || []).some((t) => t.flags?.personalize) ||
        (pg.images || []).some((im) => im.flags?.personalize) ||
        (pg.svgs || []).some((sv) => sv.flags?.personalize);
      if (hasPerso) return i;
    }
    return -1;
  }, [pages]);

  const initialPersonalizationText = useMemo(() => {
    if (personalizationPageIndex >= 0 && pages[personalizationPageIndex] && Array.isArray(pages[personalizationPageIndex].personalizacja)) {
      const guestsFromProps = pages[personalizationPageIndex].personalizacja;
      console.log(`[useEditorPersonalization] Reading initial guests from pages prop (page index ${personalizationPageIndex}):`, guestsFromProps);
      return personalizationToText(guestsFromProps);
    } else {
      console.log("[useEditorPersonalization] No initial personalization data found in pages prop.");
      return "";
    }
  }, [pages, personalizationPageIndex]);

  // Stan dla tekstu personalizacji, inicjalizowany i aktualizowany na podstawie `pages`
  const [personalizationText, setPersonalizationText] = useState(initialPersonalizationText);

  // Efekt do aktualizacji stanu, jeśli `initialPersonalizationText` (z `pages`) się zmieni
  useEffect(() => {
    // Porównaj, aby uniknąć niepotrzebnych aktualizacji i pętli
    if (personalizationText !== initialPersonalizationText) {
       console.log("[useEditorPersonalization] Updating personalizationText state based on changed initialPersonalizationText from props.");
       setPersonalizationText(initialPersonalizationText);
    }
  }, [initialPersonalizationText]); // Zależność tylko od obliczonej wartości początkowej

  // --- USUNIĘTO stary useEffect zależny od pages ---
  /*
  useEffect(() => {
    if (!pages || pages.length === 0) {
      setPersonalizationPageIndex(-1); // Resetuj, jeśli nie ma stron
      return;
    }
    let foundPageIndex = -1;
    for (let i = 0; i < pages.length; i++) {
      const pg = pages[i];
      const hasPerso =
        (pg.texts || []).some((t) => t.flags?.personalize) ||
        (pg.images || []).some((im) => im.flags?.personalize) ||
        (pg.svgs || []).some((sv) => sv.flags?.personalize);

      if (hasPerso) {
        foundPageIndex = i;
        break;
      }
    }
  */

  // Czy jest jakikolwiek obiekt z personalizacją w całym projekcie LUB czy istnieje starsza tablica 'personalizacja' (bez zmian)
  const hasPersonalize = useMemo(() => {
    if (!pages || pages.length === 0) return false;
    return pages.some((pg) => {
      const hasPersoFlag =
        (pg.texts || []).some((t) => t.flags?.personalize) ||
        (pg.images || []).some((im) => im.flags?.personalize) ||
        (pg.svgs || []).some((sv) => sv.flags?.personalize);
      // Usunięto sprawdzanie Array.isArray(pg.personalizacja)
      // Flaga hasPersonalize zależy teraz wyłącznie od obecności flagi personalize w obiektach.
      return hasPersoFlag;
    });
  }, [pages]);

  // --- ZMIANA w funkcjach zapisu: Aktualizują pole 'personalizacja' w pages ---
  // Funkcja przygotowująca zaktualizowane strony z listą gości
  const getUpdatedPagesWithGuests = useCallback(() => {
    // *** NOWY LOG: Sprawdź stan 'pages' używany przez tę funkcję ***
    console.log('[useEditorPersonalization getUpdatedPagesWithGuests] Using pages state:', JSON.stringify(pages, null, 2));
    // *** KONIEC NOWEGO LOGU ***
    if (personalizationPageIndex < 0) {
      // Jeśli nie ma strony do personalizacji, zwróć oryginalne strony
      // lub dodaj pole personalizacja do pierwszej strony, jeśli to wymagane
      console.warn("[useEditorPersonalization] No personalization page index found. Saving without updating guest list in pages.");
      // Opcjonalnie: dodaj pole do pierwszej strony, jeśli go nie ma
      // if (pages.length > 0 && !pages[0].personalizacja) {
      //    return pages.map((pg, idx) => idx === 0 ? { ...pg, personalizacja: personalizationText.split("\n").filter(Boolean) } : pg);
      // }
      return pages;
    }

    return pages.map((pg, idx) => {
      if (idx === personalizationPageIndex) {
        // Zawsze aktualizuj pole personalizacja na znalezionej stronie
        return {
          ...pg,
          personalizacja: textToPersonalization(personalizationText, pg.personalizacja) // Usuń puste linie
        };
      }
      // Jeśli strona nie ma pola personalizacja, a powinna (bo to strona personalizacji), dodaj je
      // To zabezpieczenie na wypadek, gdyby historia nie miała tego pola
      // if (idx === personalizationPageIndex && !pg.personalizacja) {
      //    return { ...pg, personalizacja: personalizationText.split("\n").filter(Boolean) };
      // }
      return pg;
    });
  }, [personalizationPageIndex, personalizationText, pages]);


  // Funkcja zapisu lokalnego (dla personalizacji) - wywołuje doAutoSave z Editor.js
  const doAutoSaveLocal = useCallback(() => {
    const updatedPages = getUpdatedPagesWithGuests();
    // Sprawdź, czy faktycznie coś się zmieniło w porównaniu do oryginalnych `pages`
    // (proste porównanie JSON może być niewystarczające przy głębokich strukturach, ale tu powinno wystarczyć)
    if (JSON.stringify(updatedPages) !== JSON.stringify(pages)) {
       console.log("[useEditorPersonalization] doAutoSaveLocal: Pages changed, calling doAutoSave.");
       doAutoSave(updatedPages); // Przekaż zaktualizowane strony do funkcji nadrzędnej
    } else {
        console.log("[useEditorPersonalization] doAutoSaveLocal: No changes detected, skipping auto-save.");
    }
  }, [getUpdatedPagesWithGuests, doAutoSave, pages]); // Dodano 'pages' do zależności

  // Funkcja zapisu ręcznego (dla personalizacji) - wywołuje handleManualSave z Editor.js
  const handleManualSaveLocal = useCallback(() => {
    // *** NOWY LOG: Sprawdź stan 'pages' przed aktualizacją personalizacji ***
    console.log('[useEditorPersonalization handleManualSaveLocal] Using pages state before update:', JSON.stringify(pages, null, 2));
    // *** KONIEC NOWEGO LOGU ***
    let updatedPages = pages; // Start with current pages
    if (personalizationPageIndex >= 0) {
       console.log('[useEditorPersonalization handleManualSaveLocal] Updating personalization field for page index:', personalizationPageIndex);
       updatedPages = pages.map((pg, idx) => {
         if (idx === personalizationPageIndex) {
           return {
             ...pg,
             personalizacja: textToPersonalization(personalizationText, pg.personalizacja)
           };
         }
         return pg;
       });
    } else {
       console.warn("[useEditorPersonalization handleManualSaveLocal] No personalization page index found.");
    }

    console.log("[useEditorPersonalization] handleManualSaveLocal: Calling handleManualSave with updated pages (potentially modified).");
    handleManualSave(updatedPages); // Przekaż zaktualizowane strony do funkcji nadrzędnej
  }, [personalizationPageIndex, personalizationText, pages, handleManualSave]); // Zaktualizowano zależności

  // Pomocnicza do „podświetlania” aktywnego panelu w lewym toolbarze
  // Rozszerzamy logikę, aby uwzględnić nowy tryb 'projectSupport'
  const panelModeForPersonalize = useCallback(
    (mode) => {
      // Jeśli sprawdzamy tryb 'personalize', a aktywny jest 'projectSupport', to 'personalize' nie jest podświetlony
      if (mode === "personalize" && panelMode === "projectSupport") {
        return false;
      }
      // Jeśli sprawdzamy tryb 'projectSupport', a aktywny jest 'personalize', to 'projectSupport' nie jest podświetlony
      if (mode === "projectSupport" && panelMode === "personalize") {
        return false;
      }
      return panelMode === mode;
    },
    [panelMode]
  );

  // Klik w menu lewym – jeśli ktoś klika w personalizację lub projectSupport, resetujemy ewentualny selection
  const handleLeftMenuClick = useCallback(
    (newMode) => {
      // Jeśli kliknięto na już aktywny tryb (z wyjątkiem tych, które mogą mieć pod-stany), można go wyłączyć
      // lub po prostu ustawić - na razie proste ustawienie nowego trybu.
      setPanelMode(newMode);
      setSelectedShapeId(null); // Zawsze resetuj zaznaczenie przy zmianie głównego trybu panelu
    },
    [setPanelMode, setSelectedShapeId]
  );

  return {
    personalizationText,
    setPersonalizationText,
    hasPersonalize,
    panelModeForPersonalize,
    handleLeftMenuClick,
    handleManualSaveLocal
    // isLoadingGuests usunięte - nie jest już potrzebne
  };
}

// Eksport funkcji pomocniczych dla innych komponentów
export {
  personalizationToText,
  textToPersonalization,
  hasQrUrl
};
